import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { useGetChatSoundsQuery } from "../../../../app/services/book/chatSounds"

const EMPTY_SOUNDS = []
const DEFAULT_LIMITS = { maxBytes: 512 * 1024, maxCount: 20 }

// Выбор звука администратор может поменять в любой момент, а у остальных пользователей
// страница при этом остаётся открытой — подтягиваем настройки раз в 10 минут.
const REFRESH_INTERVAL_MS = 10 * 60 * 1000

// Сообщение об ошибке: сервер уже отдаёт его на языке пользователя ({ error: "..." }),
// иначе берём текст самой ошибки или переданный (уже переведённый) запасной вариант.
export const getErrorMessage = (err, fallback) =>
  err?.data?.error || err?.error || err?.message || fallback

/**
 * Библиотека звуков компании и выбранные у компании звуки.
 * Запрос общий (кэш RTK Query), поэтому хук можно вызывать из нескольких мест —
 * лишних запросов не будет.
 * skip: true — не грузить (например, пока звук выключен и панель настроек закрыта).
 *
 * settings: { users, system } — что выбрано у компании ("soft", "custom:12" или null = по умолчанию).
 */
export const useCompanySounds = ({ skip = false } = {}) => {
  const { t } = useTranslation()
  const { data, isFetching, error, refetch } = useGetChatSoundsQuery(undefined, {
    skip,
    pollingInterval: skip ? 0 : REFRESH_INTERVAL_MS,
    refetchOnFocus: true,
  })

  return useMemo(
    () => ({
      sounds: data?.sounds ?? EMPTY_SOUNDS,
      settings: {
        users: data?.settings?.users_sound ?? null,
        system: data?.settings?.system_sound ?? null,
      },
      canManage: Boolean(data?.can_manage),
      limits: {
        maxBytes: data?.limits?.max_bytes ?? DEFAULT_LIMITS.maxBytes,
        maxCount: data?.limits?.max_count ?? DEFAULT_LIMITS.maxCount,
      },
      loaded: Boolean(data),
      loading: isFetching && !data,
      error: error
        ? getErrorMessage(error, t("admin.sound_err_load", "Не удалось загрузить звуки компании"))
        : null,
      refetch,
    }),
    [data, isFetching, error, refetch, t]
  )
}

export default useCompanySounds
